import { ClipboardEvent, FC, KeyboardEvent, useEffect, useRef } from "react";
import { ValidatedInputProps, InputCommonProps } from "../Inputs/Common";
import { useForceUpdate } from "../../Hooks";
import { classNames } from "../../Utilities";
import commonStyles from "../Inputs/Common.module.scss";
import styles from "./VerificationCodeInput.module.scss";

export interface VerificationCodeInputProps extends ValidatedInputProps, InputCommonProps {
    value: string;
    onChange: (value: string) => void;
    autoFocus?: boolean;
    length?: number;
}

export const VerificationCodeInput: FC<VerificationCodeInputProps> = (props) => {
    const {
        validator,
        validations,
        customMessages,
        label,
        value,
        onChange,
        autoFocus,
        length = 6
    } = props;

    const forceUpdate = useForceUpdate();
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    useEffect(() => {
        validator?.register({
            key: props.id,
            validations: validations,
            value: value,
            forceUpdate: forceUpdate,
            validationMessages: customMessages
        });

        if (autoFocus) inputRefs.current[0]?.focus();

        return () => {
            validator?.unregister(props.id);
        };
    }, []);

    useEffect(() => {
        if (validator) {
            validator.setValue(props.id, value);
            forceUpdate();
        }
    }, [value]);

    const isErrorState = (): boolean => {
        return !!validator && validator.submitAttempted() && validator.getErrors(props.id).length > 0;
    };

    const digits = Array.from({ length }, (_, i) => value[i] ?? "");

    const setDigit = (index: number, digit: string) => {
        const newDigits = [...digits];
        newDigits[index] = digit;
        onChange(newDigits.join(""));
    };

    const handleChange = (index: number, rawValue: string) => {
        const digit = rawValue.replace(/[^\d]/, "").slice(-1);

        setDigit(index, digit);

        if (digit && index < length - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Backspace") {
            if (digits[index]) {
                setDigit(index, "");
            }
            else if (index > 0) {
                inputRefs.current[index - 1]?.focus();
                setDigit(index - 1, "");
            }
            e.preventDefault();
        }
        else if (e.key === "ArrowLeft" && index > 0) {
            inputRefs.current[index - 1]?.focus();
            e.preventDefault();
        }
        else if (e.key === "ArrowRight" && index < length - 1) {
            inputRefs.current[index + 1]?.focus();
            e.preventDefault();
        }
    };

    const handlePaste = (index: number, e: ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();

        const pasted = e.clipboardData.getData("text").replace(/[^\d]/g, "");

        if (!pasted) return;

        const newDigits = [...digits];
        let target = index;

        for (const char of pasted) {
            if (target >= length) break;
            newDigits[target] = char;
            target++;
        }

        onChange(newDigits.join(""));

        const focusIndex = Math.min(target, length - 1);
        inputRefs.current[focusIndex]?.focus();
    };

    return (
        <div className={commonStyles.container}>
            {!!label && (
                <div className={classNames([commonStyles.label, isErrorState() && commonStyles.error])}>
                    <label data-required={props.validations?.required} htmlFor={props.id}>{label}</label>
                </div>
            )}

            <div className={styles.boxes}>
                {digits.map((digit, index) => (
                    <input
                        key={index}
                        ref={el => { inputRefs.current[index] = el; }}
                        className={classNames([styles.box, isErrorState() && styles.error])}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={e => handleChange(index, e.target.value)}
                        onKeyDown={e => handleKeyDown(index, e)}
                        onPaste={e => handlePaste(index, e)}
                        aria-label={`${props.id}-${index + 1}`}
                    />
                ))}
            </div>

            {isErrorState() && (
                <div className={commonStyles.validation}>
                    {validator?.getErrors(props.id).map((message) => {
                        return <div key={message}>{message}</div>;
                    })}
                </div>
            )}
        </div>
    );
};
